import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { Archive, ChevronLeft, Edit, ExternalLink, RotateCcw, ShieldAlert, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { commandCenterApi } from '../services/api'
import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  Field,
  PageHeader,
  PageLayout,
  StatePanel,
  StatusBadge,
  Textarea,
} from './common'
import { getApprovalReadiness } from './command-center/approvals/postGroupDecisionModel'
import { resolveCommandCenterDestinationDocumentV2 } from './CommandCenterPostComposerModel'
import ModerationOverrideDialog from './moderation/ModerationOverrideDialog'
import { moderationHoldBadge } from './moderation/moderationHoldStatus'
import { postGroupHeldNotice } from './moderation/postGroupModerationModel'
import styles from './CommandCenterPostGroupDetail.module.css'

function formatDate(value) {
  if (!value) return '—'
  return new Date(value).toLocaleString()
}

function pluralize(count, singular) {
  return `${count} ${singular}${count === 1 ? '' : 's'}`
}

function mutationError(error) {
  return error?.response?.data?.message
    || error?.response?.data?.error
    || error?.message
    || 'The distributed post was not changed.'
}

function lifecycleAction(group) {
  if (group.lifecycle === 'archived_settled') return null
  if (group.lifecycle === 'pending_delete') return 'undo-delete'
  if (group.lifecycle === 'archived_manual') return 'restore'
  return group.delete_capability?.allowed ? 'delete' : 'archive'
}

function describeOverrideField(field, effectiveDocument) {
  const value = effectiveDocument?.[field]
  if (field === 'media_objects') {
    return `media (${Array.isArray(value) ? value.length : 0})`
  }
  if (field === 'cta') {
    return value?.kind && value.kind !== 'none' ? `cta “${value.label || value.kind}”` : 'cta'
  }
  if (typeof value === 'string' && value) {
    return `${field} “${value}”`
  }
  return field
}

function CommandCenterPostGroupDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [action, setAction] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [previewMedia, setPreviewMedia] = useState(null)
  // The destination whose member post the moderation override dialog shows.
  const [moderationDestination, setModerationDestination] = useState(null)

  const groupQuery = useQuery({
    queryKey: ['cc-post-group', id],
    queryFn: () => commandCenterApi.getPostGroup(id),
    enabled: Boolean(id),
  })
  const detail = groupQuery.data
  const organizationsQuery = useQuery({
    queryKey: ['cc-organizations', detail?.provider_id],
    queryFn: () => commandCenterApi.listOrganizations({ provider_id: detail.provider_id }),
    enabled: Boolean(detail?.provider_id),
  })

  const actionMutation = useMutation({
    mutationFn: ({ nextAction, revision, reason = '' }) => {
      if (nextAction === 'approve') {
        return commandCenterApi.approvePostGroup(id, revision)
      }
      if (nextAction === 'reject') {
        return commandCenterApi.rejectPostGroup(id, revision, reason)
      }
      if (nextAction === 'delete') {
        return commandCenterApi.deletePostGroup(id, revision)
      }
      if (nextAction === 'undo-delete') {
        return commandCenterApi.undoPendingDeletePostGroup(id, revision)
      }
      if (nextAction === 'archive') {
        return commandCenterApi.archivePostGroup(id, revision, 'Archived by operator')
      }
      return commandCenterApi.restorePostGroup(id, revision)
    },
    onSuccess: (_data, variables) => {
      setAction(null)
      setRejectReason('')
      if (variables.nextAction === 'delete') {
        toast.success('Distributed post marked for deletion')
      } else {
        const messages = {
          approve: 'Distributed post approved',
          reject: 'Distributed post rejected',
          archive: 'Distributed post archived',
          restore: 'Distributed post restored',
          'undo-delete': 'Distributed post deletion undone',
        }
        toast.success(messages[variables.nextAction])
      }
      queryClient.invalidateQueries({ queryKey: ['cc-post-group', id] })
      queryClient.invalidateQueries({ queryKey: ['cc-post-groups'] })
      queryClient.invalidateQueries({ queryKey: ['cc-post-groups', 'pending'] })
    },
  })

  if (groupQuery.isLoading) {
    return (
      <PageLayout size="wide">
        <StatePanel state="loading" title="Loading distributed post" />
      </PageLayout>
    )
  }

  if (groupQuery.isError || !detail) {
    return (
      <PageLayout size="wide">
        <StatePanel
          state="error"
          title="Distributed post could not load"
          description={mutationError(groupQuery.error)}
          onRetry={() => groupQuery.refetch()}
        />
      </PageLayout>
    )
  }

  const shared = detail.document?.shared || {}
  const destinations = detail.destinations || []
  const media = shared.media_objects || []
  const label = shared.label || 'Untitled distributed post'
  const distinctTagCount = new Set(destinations.flatMap((destination) => destination.tag_ids || [])).size
  const organizations = organizationsQuery.data?.organizations || []
  const organizationByID = new Map(organizations.map((organization) => [organization.id, organization]))
  const regulationLabel = (destination, regulationID) => {
    const organization = organizationByID.get(destination.organization_id)
    const mapping = (organization?.regulation_mappings || [])
      .find((candidate) => candidate.offering_id === regulationID)
    return mapping?.regulation_name || regulationID
  }
  const market = shared.market || {}
  const selectionCount = new Set([
    ...(market.provider_selection_ids || []),
    ...(market.kambi_outcome_ids || []),
  ]).size + (market.wager_entries || []).length
  const archivedDestination = destinations.find((destination) => destination.archived_at || destination.archive_reason)
  const approvalReadiness = detail.approval_status === 'pending_approval'
    ? getApprovalReadiness(detail, detail)
    : { ready: true, reason: null }
  const nextAction = approvalReadiness.ready ? lifecycleAction(detail) : null
  const pendingApproval = detail.approval_status === 'pending_approval' && approvalReadiness.ready
  const actionLabel = action === 'delete' ? 'Delete' : action === 'undo-delete' ? 'Undo deletion' : action === 'archive' ? 'Archive' : 'Restore'
  const heldNotice = postGroupHeldNotice(destinations)

  return (
    <PageLayout size="wide">
      <PageHeader
        title={label}
        subtitle={`${pluralize(destinations.length, 'book')} · ${detail.provider_name}`}
        breadcrumbs={(
          <Button className={styles.backLink} variant="ghost" onClick={() => navigate('/command-center/posts')}>
            <ChevronLeft size={16} aria-hidden="true" />
            Distributed posts
          </Button>
        )}
        status={<StatusBadge auto>{detail.lifecycle}</StatusBadge>}
        primaryAction={pendingApproval ? (
          <Button
            loading={actionMutation.isPending && actionMutation.variables?.nextAction === 'approve'}
            onClick={() => {
              actionMutation.reset()
              actionMutation.mutate({ nextAction: 'approve', revision: detail.revision })
            }}
          >
            Approve distributed post
          </Button>
        ) : nextAction ? (
          <Button
            variant={nextAction === 'delete' ? 'danger' : 'secondary'}
            onClick={() => {
              actionMutation.reset()
              setAction(nextAction)
            }}
          >
            {nextAction === 'delete'
              ? <Trash2 size={16} aria-hidden="true" />
              : nextAction === 'undo-delete'
                ? <RotateCcw size={16} aria-hidden="true" />
                : nextAction === 'restore'
                  ? <RotateCcw size={16} aria-hidden="true" />
                  : <Archive size={16} aria-hidden="true" />}
            {nextAction === 'delete' ? 'Delete distributed post' : nextAction === 'undo-delete' ? 'Undo deletion' : nextAction === 'restore' ? 'Restore' : 'Archive'}
          </Button>
        ) : null}
        secondaryActions={detail.lifecycle !== 'archived_settled' ? (
          <>
            {pendingApproval ? (
              <Button
                variant="danger"
                onClick={() => {
                  actionMutation.reset()
                  setAction('reject')
                }}
              >
                Reject
              </Button>
            ) : null}
            {pendingApproval && nextAction ? (
              <Button
                variant="secondary"
                onClick={() => {
                  actionMutation.reset()
                  setAction(nextAction)
                }}
              >
                {nextAction === 'delete' ? 'Delete distributed post' : 'Archive'}
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => navigate(`/command-center/post-groups/${id}/edit`)}>
              <Edit size={16} aria-hidden="true" />
              Edit
            </Button>
          </>
        ) : null}
      />

      {actionMutation.error && action !== 'reject' ? <Alert variant="error">{mutationError(actionMutation.error)}</Alert> : null}

      {!approvalReadiness.ready ? (
        <StatePanel
          compact
          state="error"
          title="Approval details are incomplete"
          description={approvalReadiness.reason}
          onRetry={() => groupQuery.refetch()}
        />
      ) : null}
      {heldNotice ? <Alert variant="warning">{heldNotice}</Alert> : null}
      <div className={styles.reviewGrid}>
        <section className={styles.reviewPanel}>
          <h2>Shared content</h2>
          <dl className={styles.reviewList}>
            <div>
              <dt>Creator</dt>
              <dd className={styles.creatorIdentity}>
                {detail.creator?.avatar_url ? (
                  <img className={styles.creatorAvatar} src={detail.creator.avatar_url} alt="" />
                ) : null}
                <span>{detail.creator?.display_name || 'Not assigned'}</span>
              </dd>
            </div>
            <div><dt>Card title</dt><dd>{shared.title || 'Automatic'}</dd></div>
            <div><dt>Card subtitle</dt><dd>{shared.subtitle || 'Automatic'}</dd></div>
            <div><dt>Post text</dt><dd>{shared.text || 'No post text'}</dd></div>
            <div><dt>Call to action</dt><dd>{shared.cta?.kind && shared.cta.kind !== 'none' ? shared.cta.label || shared.cta.kind : 'None'}</dd></div>
          </dl>
        </section>

        <section className={styles.reviewPanel}>
          <h2>Scope</h2>
          <dl className={styles.reviewList}>
            <div><dt>Provider</dt><dd>{detail.provider_name}</dd></div>
            <div><dt>Market</dt><dd>{selectionCount ? pluralize(selectionCount, 'attached selection') : 'Content-only'}</dd></div>
            <div>
              <dt>Tags</dt>
              <dd>
                {distinctTagCount
                  ? `${pluralize(distinctTagCount, 'tag')} across ${pluralize(destinations.length, 'destination')}`
                  : 'No tags'}
              </dd>
            </div>
            <div><dt>Approval</dt><dd><StatusBadge auto>{detail.approval_status}</StatusBadge></dd></div>
          </dl>
        </section>

        <section className={styles.reviewPanelWide}>
          <h2>Destinations</h2>
          <div className={styles.destinationList}>
            {destinations.map((destination) => {
              const regulations = (destination.regulation_ids || [])
                .map((regulationID) => regulationLabel(destination, regulationID))
              const overrideFields = destination.override_fields || []
              const effectiveDocument = overrideFields.length
                ? resolveCommandCenterDestinationDocumentV2(detail, destination.organization_id)
                : null
              const overrideSummary = overrideFields.length
                ? overrideFields.map((field) => describeOverrideField(field, effectiveDocument)).join(', ')
                : null
              const moderationBadge = moderationHoldBadge(destination.moderation)
              const bookName = destination.organization_name || destination.organization_slug
              return (
                <div className={styles.destinationRow} key={destination.organization_id}>
                  <strong>{destination.organization_name || destination.organization_slug}</strong>
                  <span>
                    {pluralize(destination.tag_ids?.length || 0, 'tag')}
                    {' · '}
                    {pluralize(destination.surface_ids?.length || 0, 'surface')}
                    {' · '}
                    {regulations.length ? regulations.join(', ') : 'No regulation'}
                    {overrideSummary ? ` · Overrides: ${overrideSummary}` : ' · Shared content'}
                  </span>
                  <span className={styles.cellStack}>
                    <StatusBadge auto>{destination.lifecycle}</StatusBadge>
                    <span>{destination.approval_status}</span>
                    {moderationBadge ? <Badge compact tone={moderationBadge.tone}>{moderationBadge.label}</Badge> : null}
                    {destination.moderation && destination.post_id && destination.organization_slug ? (
                      <Button
                        size="compact"
                        variant="secondary"
                        onClick={() => setModerationDestination(destination)}
                        aria-label={`Moderation for ${bookName}`}
                      >
                        <ShieldAlert size={15} aria-hidden="true" />
                        Moderation…
                      </Button>
                    ) : null}
                  </span>
                </div>
              )
            })}
          </div>
        </section>

        {media.length ? (
          <section className={styles.reviewPanelWide}>
            <h2>Media</h2>
            <div className={styles.mediaGrid}>
              {media.map((item, index) => (
                <button
                  className={styles.mediaButton}
                  type="button"
                  key={item.id || item.asset_id || `${item.url}-${index}`}
                  onClick={() => setPreviewMedia(item)}
                >
                  {item.url ? <img className={styles.mediaThumb} src={item.url} alt="" /> : null}
                  <span>
                    Preview media
                    <ExternalLink size={14} aria-hidden="true" />
                  </span>
                </button>
              ))}
            </div>
          </section>
        ) : null}

        <section className={styles.reviewPanelWide}>
          <h2>Lifecycle audit</h2>
          <dl className={styles.reviewList}>
            <div><dt>Current lifecycle</dt><dd>{detail.lifecycle}</dd></div>
            {archivedDestination?.archive_reason ? (
              <div><dt>Archive reason</dt><dd>{archivedDestination.archive_reason}</dd></div>
            ) : null}
            {archivedDestination?.archived_at ? (
              <div><dt>Archived at</dt><dd>{formatDate(archivedDestination.archived_at)}</dd></div>
            ) : null}
            <div><dt>Created</dt><dd>{formatDate(detail.created_at)}</dd></div>
            <div><dt>Last updated</dt><dd>{formatDate(detail.updated_at)}</dd></div>
            {!detail.delete_capability?.allowed ? (
              <div>
                <dt>Permanent deletion</dt>
                <dd>{detail.delete_capability?.message || 'This distributed post must be retained.'}</dd>
              </div>
            ) : null}
          </dl>
        </section>
      </div>

      <ConfirmDialog
        open={Boolean(action && action !== 'reject')}
        title={`${actionLabel} distributed post`}
        message={action === 'delete'
          ? `Mark “${label}” and all ${destinations.length} member posts for deletion? They will be erased after the retention window.`
          : action === 'undo-delete'
            ? `Undo deletion for “${label}” and restore all ${destinations.length} member posts to their previous state?`
            : action === 'archive'
              ? `Archive “${label}” and all ${destinations.length} member posts?`
              : `Restore “${label}” and all ${destinations.length} member posts to their pre-archive state?`}
        confirmLabel={actionLabel}
        onConfirm={() => actionMutation.mutate({ nextAction: action, revision: detail.revision })}
        onCancel={() => setAction(null)}
        loading={actionMutation.isPending}
        variant={action === 'delete' ? 'danger' : 'default'}
      />
      <Dialog
        open={action === 'reject'}
        title="Reject distributed post"
        description={`Rejecting “${label}” keeps all ${destinations.length} member posts from publishing.`}
        onClose={() => {
          if (!actionMutation.isPending) {
            setAction(null)
            setRejectReason('')
          }
        }}
        dismissable={!actionMutation.isPending}
        footer={(
          <>
            <Button
              variant="secondary"
              disabled={actionMutation.isPending}
              onClick={() => {
                setAction(null)
                setRejectReason('')
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={actionMutation.isPending}
              onClick={() => actionMutation.mutate({
                nextAction: 'reject',
                revision: detail.revision,
                reason: rejectReason,
              })}
            >
              Reject distributed post
            </Button>
          </>
        )}
      >
        {actionMutation.error ? <Alert variant="error">{mutationError(actionMutation.error)}</Alert> : null}
        <Field label="Reason" hint="Optional">
          <Textarea
            value={rejectReason}
            onChange={(event) => setRejectReason(event.target.value)}
            rows={3}
          />
        </Field>
      </Dialog>

      <Dialog
        open={Boolean(previewMedia)}
        title="Media preview"
        onClose={() => setPreviewMedia(null)}
      >
        {previewMedia?.url ? (
          <img className={styles.mediaThumb} src={previewMedia.url} alt="Distributed post media preview" />
        ) : (
          <StatePanel compact state="empty" title="Media preview unavailable" />
        )}
      </Dialog>

      {moderationDestination ? (
        <ModerationOverrideDialog
          key={`${moderationDestination.organization_slug}:${moderationDestination.post_id}`}
          orgSlug={moderationDestination.organization_slug}
          postId={moderationDestination.post_id}
          postTitle={moderationDestination.title || moderationDestination.text || label}
          organizationName={moderationDestination.organization_name}
          onClose={() => {
            setModerationDestination(null)
            queryClient.invalidateQueries({ queryKey: ['cc-post-group', id] })
            queryClient.invalidateQueries({ queryKey: ['cc-post-groups'] })
          }}
        />
      ) : null}
    </PageLayout>
  )
}

export default CommandCenterPostGroupDetail
